import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Card } from '@magnetic/card'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Text } from '@magnetic/text'
import { Skeleton } from '@magnetic/skeleton'
import { listApplicationSetupAttempts } from '@/lib/api'
import { formatTimestamp } from '@/lib/protobuf-utils'

interface ApplicationSetupAttemptsCardProps {
  applicationId: string
}

function statusColor(status: string): string {
  switch (status) {
    case 'succeeded':
      return 'var(--mds-color-theme-text-success-normal)'
    case 'failed':
      return 'var(--mds-color-theme-text-error-normal)'
    case 'running':
      return 'var(--mds-color-theme-text-warning-normal)'
    default:
      return 'var(--mds-color-theme-text-secondary-normal)'
  }
}

function workflowLabel(workflow: string): string {
  switch (workflow) {
    case 'post_create_setup':
      return 'Post-create setup'
    case 'run_setup':
      return 'Run setup'
    default:
      return workflow
  }
}

export function ApplicationSetupAttemptsCard({ applicationId }: ApplicationSetupAttemptsCardProps) {
  const { data, isLoading, error } = useConnectQuery(
    listApplicationSetupAttempts,
    { applicationId, limit: 10 },
    {
      enabled: !!applicationId,
      refetchInterval: 15000,
    }
  )

  const attempts = data?.attempts ?? []

  return (
    <Card>
      <Flex direction="vertical" gap="md">
        <Heading size="sub-section">Setup Attempts</Heading>

        {isLoading ? (
          <Flex direction="vertical" gap="sm">
            <Skeleton height="sm" />
            <Skeleton height="sm" />
            <Skeleton height="sm" />
          </Flex>
        ) : error ? (
          <Text size="p4" color="light">
            {error instanceof Error ? error.message : 'Failed to load setup attempts'}
          </Text>
        ) : attempts.length === 0 ? (
          <Text size="p4" color="light">No setup attempts recorded yet.</Text>
        ) : (
          <Flex direction="vertical" gap="sm">
            {attempts.map((attempt) => (
              <Flex
                key={attempt.id}
                direction="vertical"
                gap="xs"
                style={{
                  border: '1px solid var(--mds-color-theme-outline-secondary-normal)',
                  borderRadius: 8,
                  padding: 12,
                }}
              >
                <Flex align="center" justify="space-between">
                  <Text size="p3" weight="semi-bold">{workflowLabel(attempt.workflow)}</Text>
                  <Text
                    size="p4"
                    weight="semi-bold"
                    style={{ color: statusColor(attempt.status), textTransform: 'capitalize' }}
                  >
                    {attempt.status}
                  </Text>
                </Flex>
                <Text size="p4" color="light">
                  Attempt {attempt.attempts} of {attempt.maxAttempts}
                  {attempt.nextAttemptAt ? ` - Next retry ${formatTimestamp(attempt.nextAttemptAt)}` : ''}
                </Text>
                <Text size="p4" color="light">
                  Triggered by {attempt.triggeredBy || 'system'} - {attempt.triggerReason || 'setup requested'}
                </Text>
                <Text size="p4" color="light">
                  Started {attempt.startedAt ? formatTimestamp(attempt.startedAt) : formatTimestamp(attempt.createdAt)}
                </Text>
                {attempt.errorMessage ? (
                  <Text size="p4" style={{ color: 'var(--mds-color-theme-text-error-normal)' }}>
                    {attempt.errorMessage}
                  </Text>
                ) : null}
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
    </Card>
  )
}
